import { DEFAULT_LOCALE, type Locale } from '@diana/shared'

/**
 * Strings for the classroom shell (sidebar, tab bar) and the Start page, in
 * the three languages the student panel speaks. Same pattern as copy.ts: one
 * typed object per locale, so a missing translation fails the build.
 */

/** Polish, Ukrainian and Russian count the same way: 1 · 2–4 · 5+ (and 12–14 like 5). */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (n === 1) return one
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return few
  return many
}

export type ShellStrings = {
  nav: {
    home: string
    courses: string
    homework: string
    review: string
    glossary: string
    lessons: string
    account: string
  }
  /** The phone tab bar has room for one short word each. */
  tabs: { home: string; courses: string; homework: string; review: string; lessons: string }
  navLabel: string
  accountHint: string
  backToStudio: string
  search: { placeholder: string; label: string }
  home: {
    greeting: (name: string) => string
    streak: (days: number) => string
    heroLine: (course: string, n: number, total: number) => string
    continueLesson: string
    startLesson: string
    allDone: string
    today: string
    review: string
    words: (n: number) => string
    minutes: (n: number) => string
    repeat: string
    homework: string
    homeworkMeta: (parts: number) => string
    open: string
    lessonWithDiana: string
    details: string
    nothingToday: string
    yourCourses: string
    ofLessons: (done: number, total: number) => string
    notStarted: (total: number) => string
    noCourses: string
    noCoursesHint: string
    summary: (parts: string[]) => string
    summaryLesson: (n: number) => string
    summaryReviews: (n: number) => string
    wholeCourse: string
  }
  course: {
    myCourses: string
    block: (n: number, title: string) => string
    lessonsCount: (n: number) => string
    progress: (done: number, total: number) => string
    lesson: (n: number) => string
    now: string
    done: string
    next: (n: number, title: string) => string
    continue: string
    start: string
    materials: string
    download: string
    glossary: string
    glossaryHint: string
    finished: string
  }
  homework: {
    topNote: string
    sub: string
    todo: string
    doing: string
    done: string
    partsMeta: (n: number) => string
    progress: (d: number, n: number) => string
    score: (s: number, m: number) => string
    doneNoScore: string
    start: string
    resume: string
    view: string
    primaryStart: (n: number) => string
    primaryResume: (n: number) => string
    empty: string
    emptyHint: string
    lessonTop: (n: number) => string
    noDeadline: string
    heading: string
    intro: (n: number) => string
    backToLesson: string
    check: string
    finished: string
    allDone: string
    toList: string
  }
  review: {
    end: string
    progress: string
    counter: (i: number, n: number, total: number) => string
    lesson: (level: string, n: number) => string
    tap: string
    again: string
    hard: string
    good: string
    easy: string
    when: (days: number) => string
    keys: string
    doneToday: string
    summary: (n: number) => string
    nothingDue: string
    comeBack: string
    restart: string
    home: string
    empty: string
    emptyHint: string
  }
  account: {
    sub: string
    data: string
    name: string
    email: string
    emailHint: string
    language: string
    languageHint: string
    languages: { code: 'uk' | 'ru' | 'pl'; name: string; hint: string }[]
    password: string
    current: string
    next: string
    passwordHint: string
    purchases: string
    purchase: (lessons: number) => string
    bought: (date: string) => string
    noPurchases: string
    seeLessons: string
    signOut: string
    save: string
    saving: string
    saved: string
    wrongPassword: string
  }
  glossary: {
    sub: string
    search: string
    all: string
    lesson: (n: number) => string
    inReview: string
    add: string
    listen: string
    inList: (n: number) => string
    review: string
    none: string
    noneHint: string
    nothingFound: (q: string) => string
  }
  lessons: {
    package: (bought: number, left: number) => string
    left: string
    noPackage: string
    next: string
    join: string
    cancel: string
    cancelRule: string
    cancelTooLate: string
    cancelled: string
    online: string
    upcoming: string
    history: string
    status: Record<string, string>
    noLessons: string
    noLessonsHint: string
    more: string
    book: string
    moreTime: string
  }
  /** „Nasz plan” on Lekcje z Dianą: what Diana has released from the courses for your classes. */
  plan: {
    title: string
    goal: string
    with: (names: string) => string
    empty: string
    wholeCourse: string
    forClass: (day: string) => string
    done: string
    started: string
    fresh: string
    next: string
    open: string
    lessonsOf: (done: number, all: number) => string
    homework: (done: number, parts: number) => string
    continueAt: (n: number) => string
    /** On a course Diana opened only part of. */
    partOfCourse: string
    /** On the next-class card: the plan lesson tied to that class. */
    onClass: string
  }
}

const pl: ShellStrings = {
  nav: {
    home: 'Strona główna',
    courses: 'Moje kursy',
    homework: 'Zadania domowe',
    review: 'Powtórki',
    glossary: 'Słownik',
    lessons: 'Lekcje z Dianą',
    account: 'Konto',
  },
  tabs: { home: 'Główna', courses: 'Kursy', homework: 'Zadania', review: 'Powtórki', lessons: 'Lekcje' },
  navLabel: 'Panel ucznia',
  accountHint: 'Konto i ustawienia',
  backToStudio: 'Wróć do Studia',
  search: { placeholder: 'Szukaj słowa…', label: 'Szukaj słowa' },
  home: {
    greeting: (name) => `Cześć, ${name}!`,
    streak: (d) => `Uczysz się ${d} ${plural(d, 'dzień', 'dni', 'dni')} z rzędu.`,
    heroLine: (c, n, t) => `${c} · lekcja ${n} z ${t}`,
    continueLesson: 'Kontynuuj lekcję',
    startLesson: 'Zacznij lekcję',
    allDone: 'Cały kurs za Tobą. Gratulacje!',
    today: 'Na dziś',
    review: 'Powtórki',
    words: (n) => `${n} ${plural(n, 'słówko', 'słówka', 'słówek')}`,
    minutes: (n) => `ok. ${n} ${plural(n, 'minuta', 'minuty', 'minut')}`,
    repeat: 'Powtórz',
    homework: 'Zadanie domowe',
    homeworkMeta: (p) => `${p} ${plural(p, 'część', 'części', 'części')} · bez terminu`,
    open: 'Otwórz',
    lessonWithDiana: 'Lekcja z Dianą',
    details: 'Szczegóły',
    nothingToday: 'Na dziś nic więcej. Wróć jutro.',
    yourCourses: 'Twoje kursy',
    ofLessons: (d, t) => `${d} z ${t} lekcji`,
    notStarted: (t) => `${t} lekcji · jeszcze nie zaczęty`,
    noCourses: 'Nie masz jeszcze żadnego kursu.',
    noCoursesHint: 'Kurs pojawi się tutaj, kiedy Diana go dla Ciebie otworzy.',
    summary: (parts) => parts.join(' i '),
    summaryLesson: (n) => `Lekcja ${n}`,
    summaryReviews: (n) => `${n} ${plural(n, 'powtórka', 'powtórki', 'powtórek')}`,
    wholeCourse: 'Cały kurs',
  },
  course: {
    myCourses: 'Moje kursy',
    block: (n, title) => `Blok ${n} · ${title}`,
    lessonsCount: (n) => `${n} ${plural(n, 'lekcja', 'lekcje', 'lekcji')}`,
    progress: (d, t) => `${d} z ${t}`,
    lesson: (n) => `Lekcja ${n}`,
    now: 'Teraz',
    done: 'ukończona',
    next: (n, title) => `Następna: Lekcja ${n} · ${title}`,
    continue: 'Kontynuuj',
    start: 'Zacznij',
    materials: 'Materiały do pobrania',
    download: 'Pobierz',
    glossary: 'Słownik kursu',
    glossaryHint: 'Wszystkie słowa z lekcji, z tłumaczeniem',
    finished: 'Wszystkie lekcje ukończone.',
  },
  homework: {
    topNote: 'Sprawdzają się same · bez terminów',
    sub: 'Każda lekcja ma swoje zadanie: 2–3 krótkie części, ok. 15 minut.',
    todo: 'Do zrobienia',
    doing: 'W toku',
    done: 'Zrobione',
    partsMeta: (n) => `${n} ${plural(n, 'część', 'części', 'części')} · ok. 15 min`,
    progress: (d, n) => `${d} z ${n} części zrobione`,
    score: (s, m) => `${s} / ${m} dobrze`,
    doneNoScore: 'zrobione',
    start: 'Zacznij',
    resume: 'Dokończ',
    view: 'Zobacz',
    primaryStart: (n) => `Zacznij: Lekcja ${n}`,
    primaryResume: (n) => `Dokończ: Lekcja ${n}`,
    empty: 'Na razie nie masz zadań domowych.',
    emptyHint: 'Zadanie pojawia się tutaj, kiedy zaczniesz lekcję.',
    lessonTop: (n) => `Zadanie domowe · Lekcja ${n}`,
    noDeadline: 'bez terminu',
    heading: 'Zadanie domowe',
    intro: (n) => `${n} ${plural(n, 'część', 'części', 'części')} · ok. 15 minut. Sprawdza się samo — wynik widzisz od razu.`,
    backToLesson: 'Wróć do lekcji',
    check: 'Sprawdź',
    finished: 'Gotowe',
    allDone: 'Zadanie zrobione. Brawo!',
    toList: 'Wszystkie zadania',
  },
  review: {
    end: 'Zakończ',
    progress: 'Postęp sesji',
    counter: (i, n, total) => (total > n ? `${i} z ${n} (z ${total})` : `${i} z ${n}`),
    lesson: (level, n) => `${level} · lekcja ${n}`,
    tap: 'Stuknij, żeby zobaczyć tłumaczenie',
    again: 'Jeszcze raz',
    hard: 'Trudne',
    good: 'Dobrze',
    easy: 'Łatwe',
    when: (d) => (d <= 0 ? 'za chwilę' : d === 1 ? 'jutro' : d < 7 ? `za ${d} dni` : d < 14 ? 'za tydzień' : `za ${Math.round(d / 7)} tyg.`),
    keys: '**Spacja** — odwróć · **1–4** — oceń',
    doneToday: 'Gotowe na dziś!',
    summary: (n) => `${n} ${plural(n, 'słówko powtórzone', 'słówka powtórzone', 'słówek powtórzonych')}. System sam podsunie je znowu, kiedy trzeba.`,
    nothingDue: 'Na dziś nic do powtórki.',
    comeBack: 'Wróć jutro — system sam podsunie to, co warto odświeżyć.',
    restart: 'Jeszcze raz od początku',
    home: 'Wróć na stronę główną',
    empty: 'Nie masz jeszcze żadnych fiszek.',
    emptyHint: 'Słówka trafiają tutaj z lekcji i ze Słownika („+ Do powtórek”).',
  },
  account: {
    sub: 'Twoje dane, język objaśnień i lekcje, które kupiłaś/kupiłeś.',
    data: 'Dane',
    name: 'Imię i nazwisko',
    email: 'E-mail',
    emailHint: 'Adres do logowania zmienia Diana — napisz do niej.',
    language: 'Język objaśnień',
    languageHint: 'W tym języku pokazujemy gramatykę i polecenia. Polski zawsze jest pod spodem.',
    languages: [
      { code: 'uk', name: 'Українська', hint: 'Пояснення українською' },
      { code: 'ru', name: 'Русский', hint: 'Объяснения на русском' },
      { code: 'pl', name: 'Polski', hint: 'Wszystko po polsku' },
    ],
    password: 'Hasło',
    current: 'Obecne hasło',
    next: 'Nowe hasło',
    passwordHint: 'Po zmianie wylogujemy Cię z innych urządzeń.',
    purchases: 'Lekcje z Dianą — zakupy',
    purchase: (n) => (n === 1 ? '1 lekcja' : `Pakiet ${n} lekcji`),
    bought: (d) => `kupiony ${d}`,
    noPurchases: 'Na razie nie ma tu zakupów.',
    seeLessons: 'Zobacz swoje lekcje',
    signOut: 'Wyloguj się',
    save: 'Zapisz zmiany',
    saving: 'Zapisuję…',
    saved: 'Zapisane.',
    wrongPassword: 'Nie udało się zmienić hasła — sprawdź obecne hasło.',
  },
  glossary: {
    sub: 'Słowa z lekcji, które już zaczęłaś/zacząłeś. Szukaj po polsku albo w swoim języku.',
    search: 'Szukaj słowa…',
    all: 'Wszystkie',
    lesson: (n) => `Lekcja ${n}`,
    inReview: '✓ w powtórkach',
    add: '+ Do powtórek',
    listen: 'Posłuchaj',
    inList: (n) => `${n} ${plural(n, 'słowo', 'słowa', 'słów')} z tej listy w powtórkach`,
    review: 'Powtórz słówka',
    none: 'Słownik jest jeszcze pusty.',
    noneHint: 'Słowa pojawią się tutaj, kiedy zaczniesz pierwszą lekcję.',
    nothingFound: (q) => `Nie ma słowa „${q}” w Twoich lekcjach.`,
  },
  lessons: {
    package: (b, l) => `Pakiet ${b} lekcji · zostały `,
    left: '',
    noPackage: 'Lekcje indywidualne z Dianą',
    next: 'Następna lekcja',
    join: 'Dołącz do lekcji',
    cancel: 'Odwołaj',
    cancelRule: 'odwołanie do 24 h przed',
    cancelTooLate: 'Za późno, żeby odwołać tutaj — napisz do Diany.',
    cancelled: 'Lekcja odwołana.',
    online: 'online',
    upcoming: 'Nadchodzące',
    history: 'Historia',
    status: { confirmed: 'potwierdzona', pending: 'prośba — czeka na Dianę', completed: 'odbyta', cancelled: 'odwołana', no_show: 'nieobecność' },
    noLessons: 'Nie masz jeszcze umówionych lekcji.',
    noLessonsHint: 'Wybierz termin w kalendarzu Diany — potwierdzi go i lekcja pojawi się tutaj.',
    more: 'lekcja indywidualna',
    book: 'Umów kolejną lekcję',
    moreTime: 'Chcesz dodatkowy termin?',
  },
  plan: {
    title: 'Nasz plan',
    goal: 'Cel',
    with: (names) => `razem z: ${names}`,
    empty: 'Lekcje pojawią się tutaj, gdy Diana je udostępni.',
    wholeCourse: 'Cały kurs',
    forClass: (day) => `na zajęcia ${day}`,
    done: 'ukończona',
    started: 'zaczęta',
    fresh: 'nowa',
    next: 'następna',
    open: 'Otwórz',
    lessonsOf: (d, a) => `${d} z ${a} lekcji`,
    homework: (d, p) => `praca domowa ${d}/${p}`,
    continueAt: (n) => `Dalej: lekcja ${n}`,
    partOfCourse: 'Tu są lekcje z tego kursu, które Diana udostępniła Ci na zajęcia.',
    onClass: 'Na zajęciach',
  },
}

const uk: ShellStrings = {
  nav: {
    home: 'Головна',
    courses: 'Мої курси',
    homework: 'Домашні завдання',
    review: 'Повторення',
    glossary: 'Словник',
    lessons: 'Уроки з Діаною',
    account: 'Обліковий запис',
  },
  tabs: { home: 'Головна', courses: 'Курси', homework: 'Завдання', review: 'Повтор', lessons: 'Уроки' },
  navLabel: 'Кабінет учня',
  accountHint: 'Обліковий запис і налаштування',
  backToStudio: 'Назад до Студії',
  search: { placeholder: 'Шукати слово…', label: 'Шукати слово' },
  home: {
    greeting: (name) => `Привіт, ${name}!`,
    streak: (d) => `Ти вчишся ${d} ${plural(d, 'день', 'дні', 'днів')} поспіль.`,
    heroLine: (c, n, t) => `${c} · урок ${n} з ${t}`,
    continueLesson: 'Продовжити урок',
    startLesson: 'Почати урок',
    allDone: 'Увесь курс пройдено. Вітаємо!',
    today: 'На сьогодні',
    review: 'Повторення',
    words: (n) => `${n} ${plural(n, 'слово', 'слова', 'слів')}`,
    minutes: (n) => `бл. ${n} хв`,
    repeat: 'Повторити',
    homework: 'Домашнє завдання',
    homeworkMeta: (p) => `${p} ${plural(p, 'частина', 'частини', 'частин')} · без дедлайну`,
    open: 'Відкрити',
    lessonWithDiana: 'Урок з Діаною',
    details: 'Деталі',
    nothingToday: 'На сьогодні все. Повертайся завтра.',
    yourCourses: 'Твої курси',
    ofLessons: (d, t) => `${d} з ${t} уроків`,
    notStarted: (t) => `${t} ${plural(t, 'урок', 'уроки', 'уроків')} · ще не розпочато`,
    noCourses: 'У тебе ще немає жодного курсу.',
    noCoursesHint: 'Курс з’явиться тут, коли Діана відкриє його для тебе.',
    summary: (parts) => parts.join(' і '),
    summaryLesson: (n) => `Урок ${n}`,
    summaryReviews: (n) => `${n} ${plural(n, 'повторення', 'повторення', 'повторень')}`,
    wholeCourse: 'Увесь курс',
  },
  course: {
    myCourses: 'Мої курси',
    block: (n, title) => `Блок ${n} · ${title}`,
    lessonsCount: (n) => `${n} ${plural(n, 'урок', 'уроки', 'уроків')}`,
    progress: (d, t) => `${d} з ${t}`,
    lesson: (n) => `Урок ${n}`,
    now: 'Зараз',
    done: 'завершено',
    next: (n, title) => `Далі: урок ${n} · ${title}`,
    continue: 'Продовжити',
    start: 'Почати',
    materials: 'Матеріали для завантаження',
    download: 'Завантажити',
    glossary: 'Словник курсу',
    glossaryHint: 'Усі слова з уроків, з перекладом',
    finished: 'Усі уроки завершено.',
  },
  homework: {
    topNote: 'Перевіряються самі · без дедлайнів',
    sub: 'Кожен урок має своє завдання: 2–3 короткі частини, бл. 15 хвилин.',
    todo: 'Зробити',
    doing: 'У процесі',
    done: 'Зроблено',
    partsMeta: (n) => `${n} ${plural(n, 'частина', 'частини', 'частин')} · бл. 15 хв`,
    progress: (d, n) => `${d} з ${n} частин зроблено`,
    score: (s, m) => `${s} / ${m} правильно`,
    doneNoScore: 'зроблено',
    start: 'Почати',
    resume: 'Закінчити',
    view: 'Переглянути',
    primaryStart: (n) => `Почати: урок ${n}`,
    primaryResume: (n) => `Закінчити: урок ${n}`,
    empty: 'Поки що в тебе немає домашніх завдань.',
    emptyHint: 'Завдання з’явиться тут, коли почнеш урок.',
    lessonTop: (n) => `Домашнє завдання · урок ${n}`,
    noDeadline: 'без дедлайну',
    heading: 'Zadanie domowe',
    intro: (n) => `${n} ${plural(n, 'частина', 'частини', 'частин')} · бл. 15 хвилин. Перевіряється саме — результат бачиш одразу.`,
    backToLesson: 'Назад до уроку',
    check: 'Перевірити',
    finished: 'Готово',
    allDone: 'Завдання виконано. Браво!',
    toList: 'Усі завдання',
  },
  review: {
    end: 'Завершити',
    progress: 'Прогрес сесії',
    counter: (i, n, total) => (total > n ? `${i} з ${n} (з ${total})` : `${i} з ${n}`),
    lesson: (level, n) => `${level} · урок ${n}`,
    tap: 'Торкнись, щоб побачити переклад',
    again: 'Ще раз',
    hard: 'Важко',
    good: 'Добре',
    easy: 'Легко',
    when: (d) => (d <= 0 ? 'за хвилину' : d === 1 ? 'завтра' : d < 7 ? `через ${d} дн.` : d < 14 ? 'через тиждень' : `через ${Math.round(d / 7)} тиж.`),
    keys: '**Пробіл** — перевернути · **1–4** — оцінити',
    doneToday: 'На сьогодні все!',
    summary: (n) => `${n} ${plural(n, 'слово повторено', 'слова повторено', 'слів повторено')}. Система сама нагадає їх, коли треба.`,
    nothingDue: 'На сьогодні нічого повторювати.',
    comeBack: 'Повертайся завтра — система сама підкаже, що варто освіжити.',
    restart: 'Ще раз від початку',
    home: 'На головну',
    empty: 'У тебе ще немає жодної картки.',
    emptyHint: 'Слова потрапляють сюди з уроків і зі Словника («+ До повторення»).',
  },
  account: {
    sub: 'Твої дані, мова пояснень і уроки, які ти купила/купив.',
    data: 'Дані',
    name: 'Ім’я та прізвище',
    email: 'E-mail',
    emailHint: 'Адресу для входу змінює Діана — напиши їй.',
    language: 'Мова пояснень',
    languageHint: 'Цією мовою показуємо граматику й завдання. Польська завжди під нею.',
    languages: [
      { code: 'uk', name: 'Українська', hint: 'Пояснення українською' },
      { code: 'ru', name: 'Русский', hint: 'Объяснения на русском' },
      { code: 'pl', name: 'Polski', hint: 'Wszystko po polsku' },
    ],
    password: 'Пароль',
    current: 'Поточний пароль',
    next: 'Новий пароль',
    passwordHint: 'Після зміни ми вийдемо з твого облікового запису на інших пристроях.',
    purchases: 'Уроки з Діаною — покупки',
    purchase: (n) => (n === 1 ? '1 урок' : `Пакет ${n} уроків`),
    bought: (d) => `куплено ${d}`,
    noPurchases: 'Поки що покупок немає.',
    seeLessons: 'Переглянути свої уроки',
    signOut: 'Вийти',
    save: 'Зберегти зміни',
    saving: 'Зберігаю…',
    saved: 'Збережено.',
    wrongPassword: 'Не вдалося змінити пароль — перевір поточний пароль.',
  },
  glossary: {
    sub: 'Слова з уроків, які ти вже почала/почав. Шукай польською або своєю мовою.',
    search: 'Шукати слово…',
    all: 'Усі',
    lesson: (n) => `Урок ${n}`,
    inReview: '✓ у повторенні',
    add: '+ До повторення',
    listen: 'Послухати',
    inList: (n) => `${n} ${plural(n, 'слово', 'слова', 'слів')} з цього списку в повторенні`,
    review: 'Повторити слова',
    none: 'Словник поки що порожній.',
    noneHint: 'Слова з’являться тут, коли почнеш перший урок.',
    nothingFound: (q) => `Слова «${q}» немає у твоїх уроках.`,
  },
  lessons: {
    package: (b, l) => `Пакет ${b} уроків · залишилось `,
    left: '',
    noPackage: 'Індивідуальні уроки з Діаною',
    next: 'Наступний урок',
    join: 'Приєднатися до уроку',
    cancel: 'Скасувати',
    cancelRule: 'скасування до 24 год до початку',
    cancelTooLate: 'Скасувати тут уже запізно — напиши Діані.',
    cancelled: 'Урок скасовано.',
    online: 'онлайн',
    upcoming: 'Найближчі',
    history: 'Історія',
    status: { confirmed: 'підтверджено', pending: 'запит — чекає на Діану', completed: 'проведено', cancelled: 'скасовано', no_show: 'неявка' },
    noLessons: 'У тебе ще немає запланованих уроків.',
    noLessonsHint: 'Обери час у календарі Діани — вона підтвердить, і урок з’явиться тут.',
    more: 'індивідуальний урок',
    book: 'Записатися на ще один урок',
    moreTime: 'Потрібен додатковий час?',
  },
  plan: {
    title: 'Наш план',
    goal: 'Мета',
    with: (names) => `разом з: ${names}`,
    empty: 'Уроки з’являться тут, коли Діана їх відкриє.',
    wholeCourse: 'Увесь курс',
    forClass: (day) => `на заняття ${day}`,
    done: 'завершено',
    started: 'розпочато',
    fresh: 'новий',
    next: 'наступний',
    open: 'Відкрити',
    lessonsOf: (d, a) => `${d} з ${a} уроків`,
    homework: (d, p) => `домашнє завдання ${d}/${p}`,
    continueAt: (n) => `Далі: урок ${n}`,
    partOfCourse: 'Тут уроки з цього курсу, які Діана відкрила тобі для занять.',
    onClass: 'На занятті',
  },
}

const ru: ShellStrings = {
  nav: {
    home: 'Главная',
    courses: 'Мои курсы',
    homework: 'Домашние задания',
    review: 'Повторение',
    glossary: 'Словарь',
    lessons: 'Уроки с Дианой',
    account: 'Аккаунт',
  },
  tabs: { home: 'Главная', courses: 'Курсы', homework: 'Задания', review: 'Повтор', lessons: 'Уроки' },
  navLabel: 'Кабинет ученика',
  accountHint: 'Аккаунт и настройки',
  backToStudio: 'Назад в Студию',
  search: { placeholder: 'Искать слово…', label: 'Искать слово' },
  home: {
    greeting: (name) => `Привет, ${name}!`,
    streak: (d) => `Ты учишься ${d} ${plural(d, 'день', 'дня', 'дней')} подряд.`,
    heroLine: (c, n, t) => `${c} · урок ${n} из ${t}`,
    continueLesson: 'Продолжить урок',
    startLesson: 'Начать урок',
    allDone: 'Весь курс пройден. Поздравляем!',
    today: 'На сегодня',
    review: 'Повторение',
    words: (n) => `${n} ${plural(n, 'слово', 'слова', 'слов')}`,
    minutes: (n) => `ок. ${n} мин`,
    repeat: 'Повторить',
    homework: 'Домашнее задание',
    homeworkMeta: (p) => `${p} ${plural(p, 'часть', 'части', 'частей')} · без дедлайна`,
    open: 'Открыть',
    lessonWithDiana: 'Урок с Дианой',
    details: 'Подробнее',
    nothingToday: 'На сегодня всё. Возвращайся завтра.',
    yourCourses: 'Твои курсы',
    ofLessons: (d, t) => `${d} из ${t} уроков`,
    notStarted: (t) => `${t} ${plural(t, 'урок', 'урока', 'уроков')} · ещё не начат`,
    noCourses: 'У тебя ещё нет ни одного курса.',
    noCoursesHint: 'Курс появится здесь, когда Диана откроет его для тебя.',
    summary: (parts) => parts.join(' и '),
    summaryLesson: (n) => `Урок ${n}`,
    summaryReviews: (n) => `${n} ${plural(n, 'повторение', 'повторения', 'повторений')}`,
    wholeCourse: 'Весь курс',
  },
  course: {
    myCourses: 'Мои курсы',
    block: (n, title) => `Блок ${n} · ${title}`,
    lessonsCount: (n) => `${n} ${plural(n, 'урок', 'урока', 'уроков')}`,
    progress: (d, t) => `${d} из ${t}`,
    lesson: (n) => `Урок ${n}`,
    now: 'Сейчас',
    done: 'пройден',
    next: (n, title) => `Дальше: урок ${n} · ${title}`,
    continue: 'Продолжить',
    start: 'Начать',
    materials: 'Материалы для скачивания',
    download: 'Скачать',
    glossary: 'Словарь курса',
    glossaryHint: 'Все слова из уроков, с переводом',
    finished: 'Все уроки пройдены.',
  },
  homework: {
    topNote: 'Проверяются сами · без дедлайнов',
    sub: 'У каждого урока своё задание: 2–3 короткие части, ок. 15 минут.',
    todo: 'Сделать',
    doing: 'В процессе',
    done: 'Сделано',
    partsMeta: (n) => `${n} ${plural(n, 'часть', 'части', 'частей')} · ок. 15 мин`,
    progress: (d, n) => `${d} из ${n} частей сделано`,
    score: (s, m) => `${s} / ${m} правильно`,
    doneNoScore: 'сделано',
    start: 'Начать',
    resume: 'Закончить',
    view: 'Посмотреть',
    primaryStart: (n) => `Начать: урок ${n}`,
    primaryResume: (n) => `Закончить: урок ${n}`,
    empty: 'Пока у тебя нет домашних заданий.',
    emptyHint: 'Задание появится здесь, когда начнёшь урок.',
    lessonTop: (n) => `Домашнее задание · урок ${n}`,
    noDeadline: 'без дедлайна',
    heading: 'Zadanie domowe',
    intro: (n) => `${n} ${plural(n, 'часть', 'части', 'частей')} · ок. 15 минут. Проверяется само — результат видишь сразу.`,
    backToLesson: 'Назад к уроку',
    check: 'Проверить',
    finished: 'Готово',
    allDone: 'Задание сделано. Браво!',
    toList: 'Все задания',
  },
  review: {
    end: 'Закончить',
    progress: 'Прогресс сессии',
    counter: (i, n, total) => (total > n ? `${i} из ${n} (из ${total})` : `${i} из ${n}`),
    lesson: (level, n) => `${level} · урок ${n}`,
    tap: 'Нажми, чтобы увидеть перевод',
    again: 'Ещё раз',
    hard: 'Трудно',
    good: 'Хорошо',
    easy: 'Легко',
    when: (d) => (d <= 0 ? 'через минуту' : d === 1 ? 'завтра' : d < 7 ? `через ${d} дн.` : d < 14 ? 'через неделю' : `через ${Math.round(d / 7)} нед.`),
    keys: '**Пробел** — перевернуть · **1–4** — оценить',
    doneToday: 'На сегодня всё!',
    summary: (n) => `${n} ${plural(n, 'слово повторено', 'слова повторено', 'слов повторено')}. Система сама напомнит их, когда нужно.`,
    nothingDue: 'На сегодня нечего повторять.',
    comeBack: 'Возвращайся завтра — система сама подскажет, что стоит освежить.',
    restart: 'Ещё раз сначала',
    home: 'На главную',
    empty: 'У тебя ещё нет ни одной карточки.',
    emptyHint: 'Слова попадают сюда из уроков и из Словаря («+ К повторению»).',
  },
  account: {
    sub: 'Твои данные, язык объяснений и уроки, которые ты купила/купил.',
    data: 'Данные',
    name: 'Имя и фамилия',
    email: 'E-mail',
    emailHint: 'Адрес для входа меняет Диана — напиши ей.',
    language: 'Язык объяснений',
    languageHint: 'На этом языке показываем грамматику и задания. Польский всегда под ним.',
    languages: [
      { code: 'uk', name: 'Українська', hint: 'Пояснення українською' },
      { code: 'ru', name: 'Русский', hint: 'Объяснения на русском' },
      { code: 'pl', name: 'Polski', hint: 'Wszystko po polsku' },
    ],
    password: 'Пароль',
    current: 'Текущий пароль',
    next: 'Новый пароль',
    passwordHint: 'После смены мы выйдем из аккаунта на других устройствах.',
    purchases: 'Уроки с Дианой — покупки',
    purchase: (n) => (n === 1 ? '1 урок' : `Пакет из ${n} уроков`),
    bought: (d) => `куплен ${d}`,
    noPurchases: 'Пока покупок нет.',
    seeLessons: 'Посмотреть свои уроки',
    signOut: 'Выйти',
    save: 'Сохранить',
    saving: 'Сохраняю…',
    saved: 'Сохранено.',
    wrongPassword: 'Не удалось сменить пароль — проверь текущий пароль.',
  },
  glossary: {
    sub: 'Слова из уроков, которые ты уже начала/начал. Ищи по-польски или на своём языке.',
    search: 'Искать слово…',
    all: 'Все',
    lesson: (n) => `Урок ${n}`,
    inReview: '✓ в повторении',
    add: '+ К повторению',
    listen: 'Послушать',
    inList: (n) => `${n} ${plural(n, 'слово', 'слова', 'слов')} из этого списка в повторении`,
    review: 'Повторить слова',
    none: 'Словарь пока пуст.',
    noneHint: 'Слова появятся здесь, когда начнёшь первый урок.',
    nothingFound: (q) => `Слова «${q}» нет в твоих уроках.`,
  },
  lessons: {
    package: (b, l) => `Пакет из ${b} уроков · осталось `,
    left: '',
    noPackage: 'Индивидуальные уроки с Дианой',
    next: 'Следующий урок',
    join: 'Присоединиться к уроку',
    cancel: 'Отменить',
    cancelRule: 'отмена до 24 ч до начала',
    cancelTooLate: 'Отменить здесь уже поздно — напиши Диане.',
    cancelled: 'Урок отменён.',
    online: 'онлайн',
    upcoming: 'Ближайшие',
    history: 'История',
    status: { confirmed: 'подтверждён', pending: 'запрос — ждёт Диану', completed: 'проведён', cancelled: 'отменён', no_show: 'неявка' },
    noLessons: 'У тебя ещё нет запланированных уроков.',
    noLessonsHint: 'Выбери время в календаре Дианы — она подтвердит, и урок появится здесь.',
    more: 'индивидуальный урок',
    book: 'Записаться на ещё один урок',
    moreTime: 'Нужно дополнительное время?',
  },
  plan: {
    title: 'Наш план',
    goal: 'Цель',
    with: (names) => `вместе с: ${names}`,
    empty: 'Уроки появятся здесь, когда Диана их откроет.',
    wholeCourse: 'Весь курс',
    forClass: (day) => `к занятию ${day}`,
    done: 'пройден',
    started: 'начат',
    fresh: 'новый',
    next: 'следующий',
    open: 'Открыть',
    lessonsOf: (d, a) => `${d} из ${a} уроков`,
    homework: (d, p) => `домашнее задание ${d}/${p}`,
    continueAt: (n) => `Дальше: урок ${n}`,
    partOfCourse: 'Здесь уроки этого курса, которые Диана открыла тебе для занятий.',
    onClass: 'На занятии',
  },
}

const all: Record<Locale, ShellStrings> = { pl, uk, ru }

export function shellCopy(locale: Locale | undefined): ShellStrings {
  return all[locale ?? DEFAULT_LOCALE] ?? all[DEFAULT_LOCALE]
}
